import { useEffect, useRef } from 'react';
import type { PiiAccessKind } from '@kawai/shared';
import { recordPiiAccess } from '../api/auditAccess.js';

interface UsePiiAccessAuditOptions {
  /** customer_detail のとき対象 uid。 */
  targetId?: string;
  /** customer_list のとき表示件数。 */
  count?: number;
  /** データ読込が完了し、実際に PII が表示された段で true にする。 */
  enabled: boolean;
}

/**
 * S-74: PII 表示イベントを 1 回だけ監査記録するフック（DPA 第7条1項 / 別添①5.4）。
 *
 * - `enabled` が true になった最初のレンダーで `recordPiiAccess` を fire-and-forget。
 * - `kind`+`targetId` のシグネチャ単位で重複発火を防ぐ（StrictMode 二重マウント /
 *   再レンダー / count 後追い確定でも 1 回）。count は発火時点の値を採用。
 */
export function usePiiAccessAudit(
  kind: PiiAccessKind,
  { targetId, count, enabled }: UsePiiAccessAuditOptions,
): void {
  const firedRef = useRef<string>('');
  const countRef = useRef<number | undefined>(count);
  countRef.current = count;

  useEffect(() => {
    if (!enabled) return;
    const sig = `${kind}:${targetId ?? ''}`;
    if (firedRef.current === sig) return;
    firedRef.current = sig;
    void recordPiiAccess({ kind, targetId, count: countRef.current });
  }, [kind, targetId, enabled]);
}
